import { _ } from "../../../../core/polyglot/polyglot.js";
import AbstractColumnTemplate from "./AbstractColumnTemplate.js";
import PercentageColumn from "../../reports/columns/PercentageColumn.js";

/**
 * Percentage column template.
 *
 * @param {string} spec.rowTemplateId - id of row template which, after interpreting, will yield the column of the cell we compare from.
 * @param {string} spec.columnTemplateId - id of column template which, after interpreting, will yield the row of the cell we compare from.
 * @param {string} spec.makeAbsolute - if set to true this will present the absolute value for all values in the column.
 */
export default class PercentageColumnTemplate extends AbstractColumnTemplate {
	constructor({ rowTemplateId, columnTemplateId, makeAbsolute = false }) {
		super(...arguments);

		this._rowTemplateId = rowTemplateId;
		this._columnTemplateId = columnTemplateId;
		this._makeAbsolute = makeAbsolute;
	}

	getRowTemplate() {
		return this._reportTemplate
			.getRows()
			.find((row) => row.getId() === this._rowTemplateId);
	}

	setRowTemplate(row) {
		this._rowTemplateId = row.getId();
	}

	getColumnTemplate() {
		return this._reportTemplate
			.getColumns()
			.find((column) => column.getId() === this._columnTemplateId);
	}

	setColumnTemplate(column) {
		this._columnTemplateId = column.getId();
	}

	shouldMakeAbsolute() {
		return this._makeAbsolute;
	}

	setMakeAbsolute(value) {
		this._makeAbsolute = value;
	}

	validate({ collector }) {
		super.validate(...arguments);

		collector.addErrorIf(
			!this.getRowTemplate(),
			_("Percentage column with no associated row"),
		);

		collector.addErrorIf(
			!this.getColumnTemplate(),
			_("Percentage column with no associated column"),
		);
	}

	accept(visitor) {
		return visitor.visitPercentageColumnTemplate(this);
	}

	_instanceClass() {
		return PercentageColumn;
	}

	_interpretationSpec(interpreter) {
		const spec = super._interpretationSpec(...arguments);

		const rowTemplate = this.getRowTemplate();
		const columnTemplate = this.getColumnTemplate();

		spec.makeAbsolute = this.shouldMakeAbsolute();

		spec.row =
			rowTemplate && interpreter.interpretItemTemplate(rowTemplate);

		spec.column =
			columnTemplate && interpreter.interpretItemTemplate(columnTemplate);

		return spec;
	}

	_serializedProperties() {
		const properties = super._serializedProperties(...arguments);

		properties.rowTemplateId = this._rowTemplateId;
		properties.columnTemplateId = this._columnTemplateId;
		properties.makeAbsolute = this._makeAbsolute;

		return properties;
	}

	static getLabel() {
		return _("Percentage");
	}

	static getType() {
		return "PercentageColumnTemplate";
	}
}
